import { logger } from '@utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://se-demos-tools.lucidworkssales.com';

// Cache for API health status to reduce redundant calls
let healthCache = { 
  isHealthy: null, 
  lastChecked: null, 
  checking: false 
};

export const checkApiHealth = async () => {
  try {
    // Return cached result if checked within last 30 seconds
    const now = new Date();
    if (healthCache.lastChecked && 
        (now - healthCache.lastChecked) < 30000 && 
        healthCache.isHealthy !== null) {
      return { 
        isHealthy: healthCache.isHealthy, 
        lastChecked: healthCache.lastChecked 
      };
    }

    if (healthCache.checking) {
      return { 
        isHealthy: healthCache.isHealthy, 
        lastChecked: healthCache.lastChecked 
      };
    }

    healthCache.checking = true;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // Reduced timeout
    
    const response = await fetch(`${API_BASE_URL}/api/health`, {
      method: 'GET',
      signal: controller.signal,
      headers: { 'Accept': 'application/json' }
    });
    
    clearTimeout(timeoutId);
    
    const result = { isHealthy: response.ok, lastChecked: new Date() };
    healthCache = { ...result, checking: false };
    
    return result;
  } catch (error) {
    logger.error('API health check failed:', error);
    const result = { isHealthy: false, lastChecked: new Date() };
    healthCache = { ...result, checking: false };
    return result;
  }
};

export const fetchBotResponse = async (requestBody) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000); // Reduced timeout
  
  try {

    const response = await fetch(`${API_BASE_URL}/api/ask-pdf`, {
      method: 'POST',
      headers: { 
        'Accept': 'application/json', 
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache' // Prevent caching for dynamic responses
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      // Try to get error details but don't block on it
      let errorText = 'Unknown error';
      let errorData = null;
      try {
        const text = await Promise.race([
          response.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000))
        ]);
        errorText = text;
        
        // Try to parse as JSON for better error details
        try {
          errorData = JSON.parse(text);
        } catch (e) {
          // Not JSON, use text as is
        }
      } catch (e) {
        // Ignore text parsing errors
      }
      
      // Log additional context for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          errorData: errorData,
          requestBody: requestBody
        });
        
        // Special handling for 400 errors - likely validation issues
        if (response.status === 400) {
          console.error('400 Bad Request Details:', {
            url: response.url,
            headers: [...response.headers.entries()],
            requestMethod: 'POST',
            requestBodyString: JSON.stringify(requestBody, null, 2)
          });
        }
      }
      
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    // Get raw response text first to handle malformed responses
    const responseText = await response.text();
    
    // Check if response starts with "data:" prefix first
    if (responseText.includes('data:')) {
      // Handle streaming responses with multiple "data:" prefixed JSON objects
      const lines = responseText.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      const dataLines = lines.filter(line => line.startsWith('data:'));
      
      if (dataLines.length > 0) {
        // Look for the final complete response (type: "response.completed")
        let completeResponse = null;
        
        // Process lines in reverse order to find the most recent complete response first
        for (let i = dataLines.length - 1; i >= 0; i--) {
          const line = dataLines[i];
          try {
            const jsonStr = line.substring(5).trim(); // Remove "data:" prefix and trim
            
            const parsed = JSON.parse(jsonStr);
            
            if (parsed.type === 'response.completed' && parsed.predictions) {
              completeResponse = parsed;
              break;
            }
          } catch (parseError) {
            console.warn(`Failed to parse streaming line ${i}:`, parseError.message);
            console.warn('Problematic line:', line.substring(0, 100) + '...');
          }
        }
        
        if (completeResponse) {
          return completeResponse;
        } else {
          console.error('No complete response found in streaming data');
          throw new Error('Streaming response detected but no complete response found');
        }
      }
      
      // Handle single "data:" prefixed responses (non-streaming)
      let cleanedText = responseText;
      if (responseText.startsWith('data:')) {
        cleanedText = responseText.substring(5); // Remove "data:" prefix
      }
      
      // Try to find and extract valid JSON from the cleaned text
      const jsonMatch = cleanedText.match(/\{.*\}/s);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          return parsed;
        } catch (parseError) {
          console.error('Failed to parse extracted JSON:', parseError.message);
        }
      }
      
      // If all parsing attempts fail, throw an error
      throw new Error(`Invalid JSON response with data prefix. Raw response: ${responseText.substring(0, 200)}...`);
    }
    
    // Try to parse as regular JSON if no "data:" prefix
    try {
      return JSON.parse(responseText);
    } catch (jsonError) {
      console.error('JSON parsing failed:', jsonError.message);
      throw new Error(`Invalid JSON response: ${jsonError.message}. Raw response: ${responseText.substring(0, 200)}...`);
    }
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Enhanced error logging
    if (process.env.NODE_ENV === 'development') {
      console.error('fetchBotResponse error:', {
        message: error.message,
        name: error.name,
        requestBody: requestBody
      });
    }
    
    throw error;
  }
};

export const formatErrorMessage = (error) => {
  let errorMsg = error.message || 'An unexpected error occurred';
  
  if (error.name === 'AbortError') {
    return '⏱️ The AI response took longer than expected. Please try again.';
  }
  
  if (/permission|403|Forbidden/i.test(errorMsg)) {
    return '🚫 You do not have permission to access this resource.';
  }
  
  if (/CORS|Access-Control-Allow-Origin/i.test(errorMsg)) {
    return '🌐 CORS error detected. Please ensure the backend proxy is running.';
  }
  
  return errorMsg;
};

// Memoized conversation request creation to avoid redundant object creation
const requestCache = new Map();

// Function to clear cache when memory expires or for specific products
export const clearRequestCache = (productId = null) => {
  if (productId) {
    // Clear cache entries for specific product
    const keysToDelete = [];
    for (const key of requestCache.keys()) {
      if (key.startsWith(productId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => requestCache.delete(key));
    console.log(`Cleared ${keysToDelete.length} cached request entries for product: ${productId}`);
  } else {
    // Clear entire cache
    const cacheSize = requestCache.size;
    requestCache.clear();
    console.log(`Cleared entire request cache (${cacheSize} entries)`);
  }
};

export const createConversationRequest = (product, userQuestion, memoryUuid, pdfStatus) => {
  // Validate required parameters
  if (!product) {
    throw new Error('Product data is required');
  }
  
  if (!userQuestion || typeof userQuestion !== 'string') {
    throw new Error('User question is required and must be a string');
  }
  
  // Create cache key for memoization (excluding userQuestion and memoryUuid which change)
  // Never cache memoryUuid as it can expire and should not be reused
  const cacheKey = `${product?.id || product?.unique_id || 'unknown'}_${pdfStatus?.status || 'unknown'}`;
  
  const hasPdfsInGcs = pdfStatus?.status === 'all_ready' || pdfStatus?.status === 'partial_ready';
  const isFollowUp = memoryUuid !== null;
  
  // Only send PDF links during initialization (no existing memoryUuid) and limit to 3
  const shouldSendPdfs = !isFollowUp && hasPdfsInGcs && Array.isArray(product?._lw_pdf_url_ss);
  const pdfLinks = shouldSendPdfs ? product._lw_pdf_url_ss.slice(0, 3) : [];
  
  // Check cache for base structure (without memoryUuid)
  let baseStructure = requestCache.get(cacheKey);
  
  if (!baseStructure) {
    // Ensure we have a valid product ID
    const productId = product?.id || product?.unique_id || product?.product_id;
    if (!productId) {
      throw new Error('Product must have an id, unique_id, or product_id');
    }
    
    baseStructure = {
      batch: [{
        name: product?.name || 'Product Q&A Agent',
        description: product?.description || '',
        pdfLinks: pdfLinks,
        id: productId
      }],
      useCaseConfig: {
        domain: 'manufacturing'
        // Never cache memoryUuid - it should be added fresh each time
      }
    };
    
    // Cache the base structure (limit cache size)
    if (requestCache.size > 10) {
      const firstKey = requestCache.keys().next().value;
      requestCache.delete(firstKey);
    }
    requestCache.set(cacheKey, baseStructure);
  }
  
  // Always create a new object with the current query and fresh memoryUuid
  const request = {
    ...baseStructure,
    batch: [{
      ...baseStructure.batch[0],
      query: userQuestion
    }],
    useCaseConfig: {
      ...baseStructure.useCaseConfig,
      // Always add memoryUuid fresh, never from cache
      ...(isFollowUp && memoryUuid ? { memoryUuid } : {})
    }
  };
  
  return request;
};
